<template>
  <component
    :is="isButton ? 'button' : 'a'"
    :href="isButton ? null : canvasAppUrl"
    :type="isButton ? 'button' : null"
    :target="isButton ? null : '_blank'"
    :rel="isButton ? null : 'noopener noreferrer'"
    :disabled="isButton ? inactive : null"
    :class="['workflow-tuning-button', `variant-${variant}`, { 'icon-only': iconOnly }]"
    :aria-disabled="inactive ? 'true' : 'false'"
    :tabindex="!isButton && inactive ? -1 : null"
    :title="buttonTitle"
    :aria-label="buttonTitle"
    @click.stop.prevent="openWorkflow"
  >
    <i :class="loading ? 'el-icon-loading' : 'el-icon-edit-outline'" aria-hidden="true"></i>
    <span>{{ loading ? '正在创建工作流…' : label }}</span>
  </component>
</template>

<script>
import { apiRequest } from '@/services/api'
import { closestImageRatio, readImageDimensions } from '@/utils/imageAspectRatio'
import { requireWorkflowAppUrl, workflowAppUrl, workflowOpenUrl } from '@/utils/workflowAppUrl'

function outputResolution(width, height) {
  const longEdge = Math.max(Number(width) || 0, Number(height) || 0)
  if (longEdge >= 3000) return '4k'
  if (longEdge >= 1600) return '2k'
  return '1k'
}

export default {
  name: 'WorkflowTuningButton',
  props: {
    imageUrl: { type: String, default: '' },
    prompt: { type: String, default: '' },
    sourceModel: { type: String, default: '' },
    contextId: { type: String, default: '' },
    moduleName: { type: String, default: '' },
    label: { type: String, default: '工作流' },
    variant: {
      type: String,
      default: 'link',
      validator: value => ['link', 'button'].includes(value)
    },
    iconOnly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return { loading: false }
  },
  computed: {
    isButton() {
      return this.variant === 'button'
    },
    inactive() {
      return this.disabled || this.loading || !this.imageUrl
    },
    canvasAppUrl() {
      return workflowAppUrl() || '#'
    },
    resolvedModuleName() {
      return this.moduleName || (this.$route && this.$route.meta && this.$route.meta.title) || '图片生成'
    },
    buttonTitle() {
      return `对结果不满意？把图片带到无限画布做精准控制（${this.resolvedModuleName}）`
    }
  },
  methods: {
    async ensurePublicImageUrl(source) {
      const value = String(source || '').trim()
      if (!value) throw new Error('当前没有可精修的生成结果')
      let parsed
      try {
        parsed = new URL(value, window.location.origin)
      } catch (error) {
        throw new Error('生成结果地址无效，无法带入工作流')
      }
      if (['http:', 'https:'].includes(parsed.protocol)) return parsed.href
      if (!['blob:', 'data:'].includes(parsed.protocol)) throw new Error('当前图片格式暂不支持带入工作流')

      const response = await window.fetch(value)
      if (!response.ok) throw new Error('读取当前生成结果失败')
      const blob = await response.blob()
      const extension = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png'
      const file = new File([blob], `workflow-source-${Date.now()}.${extension}`, { type: blob.type || 'image/png' })
      const form = new FormData()
      form.append('file', file)
      const uploaded = await apiRequest('/api/images/upload', {
        method: 'POST',
        body: form,
        timeoutMs: 120000,
        timeoutMessage: '图片上传等待超时'
      })
      if (!uploaded.url) throw new Error('图片上传成功但没有返回可用地址')
      return new URL(uploaded.url, window.location.origin).href
    },
    async openWorkflow() {
      if (this.loading || this.disabled) return
      try {
        requireWorkflowAppUrl()
      } catch (error) {
        this.$message.error(error.message)
        return
      }
      const source = String(this.imageUrl || '').trim()
      if (!source) {
        this.$message.warning('当前没有可精修的生成结果')
        return
      }
      const workflowWindow = window.open('about:blank', '_blank')
      if (!workflowWindow) {
        this.$message.warning('新页面被浏览器拦截，请允许本站打开新页面后重试')
        return
      }
      workflowWindow.opener = null
      this.loading = true
      try {
        const dimensions = await readImageDimensions(source).catch(() => ({ width: 0, height: 0 }))
        const sourceImage = await this.ensurePublicImageUrl(source)
        const result = await apiRequest('/api/integration/projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceImage,
            prompt: String(this.prompt || '').trim() || '请在保留原图主体、构图和空间关系的基础上，按我的新要求继续优化。',
            sourceModel: this.sourceModel,
            conversationId: this.contextId,
            sourceModule: this.resolvedModuleName,
            name: `${this.resolvedModuleName} · 工作流精修`,
            size: closestImageRatio(dimensions.width, dimensions.height),
            resolution: outputResolution(dimensions.width, dimensions.height)
          }),
          timeoutMs: 30000,
          timeoutMessage: '创建工作流项目等待超时'
        })
        if (!result.openUrl) throw new Error(result.error || '工作流项目创建失败')
        workflowWindow.location.replace(workflowOpenUrl(result.openUrl))
      } catch (error) {
        workflowWindow.close()
        this.$message.error(error.message || '无法连接无限画布，请确认工作流服务已启动')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.workflow-tuning-button.variant-link {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: max-content;
  max-width: 100%;
  margin-left: auto;
  color: #356ac3 !important;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: color .18s ease;
}
.workflow-tuning-button.variant-link:hover:not([aria-disabled='true']) {
  color: #174b9f !important;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.workflow-tuning-button.variant-link:focus-visible { outline: 2px solid rgba(37, 99, 235, .24); outline-offset: 2px; }
.workflow-tuning-button.variant-link[aria-disabled='true'] { color: #9ca3af !important; cursor: not-allowed; }
.workflow-tuning-button i { flex: 0 0 auto; font-size: 14px; }
.workflow-tuning-button.icon-only > span { display: none; }
@media (prefers-reduced-motion: reduce) {
  .workflow-tuning-button { transition: none; }
}
</style>
